/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { Box, Loader, Spacer, Text } from "@nordcloud/gnui";
import { Table } from "~/components";
import { ToolsAndIntegrations as ToolsAndIntegrationsType } from "~/components/BasicCustomerInfo/types";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditDatePicker } from "~/components/InlineEdit/InlineEditDatePicker";
import { InlineEditMultiSelect } from "~/components/InlineEdit/InlineEditMultiSelect";
import { InlineEditSwitch } from "~/components/InlineEdit/InlineEditSwitch";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { AddJiraProject } from "~/components/JiraProjects/JiraProjectAdd";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { AddPagerDutyBusinessService } from "~/components/PagerDutyBusinessServices/PagerDutyBusinessServiceAdd";
import { Divider } from "~/components/Styled/Divider";
import { showSuccess } from "~/components/Toast";
import { DetailRow } from "~/components/Utils/DetailRow";
import { useCustomer } from "~/context/User/Customer";
import { useUserConsumer } from "~/context/User/User";
import { useAPIData } from "~/hooks/useAPIData";
import { JiraProject } from "~/models";
import { FleetControlTenant } from "~/models/fleetcontrol";
import { PagerDutyBusinessService } from "~/models/pagerDutyBusinessService";
import { isStandardUser } from "~/routing";
import { generateActionSuccessText, useAxios, useQueryState } from "~/tools";
import ConfluenceDuplicator from "./ConfluenceDuplicator";
import DatadogOrgs from "./DatadogOrgs";
import { JiraProjectsListColumns } from "./JiraProjectList";
import { PagerDutyBusinessServiceListColumns } from "./PagerDutyBusinessServiceList";

type RouteParams = {
  customerId: string;
};

function ToolsAndIntegrations() {
  const { customerId } = useParams<RouteParams>();
  const { customer, fetchCustomer, isCustomerLoading } = useCustomer();

  const [tenants, tenantsLoading] = useAPIData<FleetControlTenant[]>(
    `/fleet-control/tenants?customer=${customerId}`,
    (data) => data.tenants,
    []
  );

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const { showError } = useShowError();
  const { Axios } = useAxios();
  const onSubmit = async (
    key: keyof ToolsAndIntegrationsType,
    value: string | string[] | boolean | undefined | FleetControlTenant[]
  ) => {
    try {
      const updateData = {
        ...customer,
        toolsAndIntegrations: {
          ...customer.toolsAndIntegrations,
          [key]: value || null,
        },
      };
      await Axios().put(`/customers/${customer.id}`, updateData);
      showSuccess(
        generateActionSuccessText(`Customer ${customer.name}`)()(`updated`)()
      );
    } catch (e: any) {
      showError(e.response);
    }

    return fetchCustomer();
  };

  const data = customer.toolsAndIntegrations;

  // JiraProject start
  const [{ limit, page }] = useQueryState();

  type JiraProjectsState = {
    list: JiraProject[];
    totalCount: string;
  };

  type PagerDutyBusinessServicesState = {
    list: PagerDutyBusinessService[];
    totalCount: string;
  };

  const [jiraProjects, setJiraProjects] = React.useState<JiraProjectsState>({
    list: [],
    totalCount: "",
  });

  const [
    pagerDutyBusinessServices,
    setPagerDutyBusinessServices,
  ] = React.useState<PagerDutyBusinessServicesState>({
    list: [],
    totalCount: "0",
  });

  const fetchJiraProjects = React.useCallback(async () => {
    try {
      setJiraProjects(() => ({
        list: customer.toolsAndIntegrations?.jiraProjects ?? [],
        totalCount:
          customer.toolsAndIntegrations?.jiraProjects?.toString() ?? "",
      }));
    } catch (e: any) {
      showError(e.response);
    } finally {
      // setIsLoading(false);
    }
  }, [
    Axios,
    customerId,
    limit,
    page,
    showError,
    customer.toolsAndIntegrations?.jiraProjects,
  ]);

  const fetchPagerDutyBusinessServices = React.useCallback(async () => {
    try {
      const rawData = customer.toolsAndIntegrations?.pagerDutyCustomers;
      const normalizedList: PagerDutyBusinessService[] = Array.isArray(rawData)
        ? rawData
        : [];

      setPagerDutyBusinessServices({
        list: normalizedList,
        totalCount: normalizedList.length.toString(),
      });
    } catch (e: any) {
      showError(e.response);
    }
  }, [customer.toolsAndIntegrations, showError]);

  const removePDBusinessService = React.useCallback(
    async (id: string) => {
      try {
        const updateData = { ...customer };
        const tools =
          updateData.toolsAndIntegrations ?? ({} as ToolsAndIntegrationsType);
        updateData.toolsAndIntegrations = tools;

        const existingServices: PagerDutyBusinessService[] = Array.isArray(
          tools.pagerDutyCustomers
        )
          ? tools.pagerDutyCustomers
          : [];

        tools.pagerDutyCustomers = existingServices.filter((p) => p.id !== id);

        await Axios().put(`/customers/${customerId}`, updateData);
        fetchCustomer();
        fetchPagerDutyBusinessServices();
        showSuccess(
          generateActionSuccessText(`PagerDuty Business Service`)()(`removed`)()
        );
      } catch (e: any) {
        showError(e.response);
      }
    },
    [
      customer,
      customerId,
      showError,
      Axios,
      fetchCustomer,
      fetchPagerDutyBusinessServices,
    ]
  );

  const columns = React.useMemo(
    () => JiraProjectsListColumns(fetchJiraProjects, canEdit),
    [canEdit, fetchJiraProjects]
  );

  const pagerDutyBusinessServiceColumns = React.useMemo(
    () =>
      PagerDutyBusinessServiceListColumns(
        fetchPagerDutyBusinessServices,
        canEdit,
        removePDBusinessService,
        fetchCustomer
      ),
    [
      canEdit,
      fetchPagerDutyBusinessServices,
      removePDBusinessService,
      fetchCustomer,
    ]
  );

  React.useEffect(() => {
    fetchJiraProjects();
    fetchPagerDutyBusinessServices();
  }, [fetchJiraProjects, fetchPagerDutyBusinessServices]);

  // Jira Project end

  return (
    <LoaderWrap loading={isCustomerLoading}>
      <Box spacing="spacing04">
        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            PagerDuty Business Services
          </Text>
          {canEdit && (
            <AddPagerDutyBusinessService
              refetch={fetchPagerDutyBusinessServices}
              fetchCustomer={fetchCustomer}
            />
          )}
          <Box>
            <Table
              columns={pagerDutyBusinessServiceColumns}
              data={pagerDutyBusinessServices.list || []}
              noDataText="No PagerDuty Business Services added."
            />
          </Box>
          <Spacer height="1rem" />
        </Divider>

        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            Datadog
          </Text>

          <DatadogOrgs
            customer={customer}
            fetchCustomer={fetchCustomer}
            canEdit={canEdit}
          />
          <Spacer height="1rem" />
        </Divider>

        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            Estate Helper
          </Text>

          <DetailRow title="Organization ID">
            <InlineEditText
              name="estateHelperOrganizationId"
              defaultValue={data?.estateHelperOrganizationId}
              onSubmit={(val) => onSubmit("estateHelperOrganizationId", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow
            title="Reporting Tenant ID"
            tooltip="May differ from the associated tenant ID."
          >
            <InlineEditText
              name="estateHelperReportingTenantId"
              defaultValue={data?.estateHelperReportingTenantId}
              onSubmit={(val) => onSubmit("estateHelperReportingTenantId", val)}
              href={`${process.env.REACT_APP_KLARITY_URL}/?customerId=${data?.estateHelperReportingTenantId}`}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting start date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.estateHelperReportingStartDate}
              onSubmit={(val) =>
                onSubmit("estateHelperReportingStartDate", val)
              }
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting end date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.estateHelperReportingEndDate}
              onSubmit={(val) => onSubmit("estateHelperReportingEndDate", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Service discovery">
            <InlineEditSwitch
              defaultValue={data?.estateHelperServiceDiscovery || false}
              onSubmit={(val) => onSubmit("estateHelperServiceDiscovery", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <Spacer height="1rem" />
        </Divider>

        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            AutoBackup
          </Text>

          <DetailRow title="Customer ID">
            <InlineEditText
              name="autoBackupCustomerId"
              defaultValue={data?.autoBackupCustomerId}
              onSubmit={(val) => onSubmit("autoBackupCustomerId", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting start date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.autoBackupReportingStartDate}
              onSubmit={(val) => onSubmit("autoBackupReportingStartDate", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting end date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.autoBackupReportingEndDate}
              onSubmit={(val) => onSubmit("autoBackupReportingEndDate", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <Spacer height="1rem" />
        </Divider>

        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            AutoPatcher
          </Text>

          <DetailRow title="Customer ID">
            <InlineEditText
              name="autoPatcherCustomerId"
              defaultValue={data?.autoPatcherCustomerId}
              onSubmit={(val) => onSubmit("autoPatcherCustomerId", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting start date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.autoPatcherReportingStartDate}
              onSubmit={(val) => onSubmit("autoPatcherReportingStartDate", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting end date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.autoPatcherReportingEndDate}
              onSubmit={(val) => onSubmit("autoPatcherReportingEndDate", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <Spacer height="1rem" />
        </Divider>

        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            Fleet Control
          </Text>

          <DetailRow title="Customer ID">
            {tenantsLoading ? (
              <>
                <Loader inContent />
                <Spacer height="1rem" />
              </>
            ) : (
              <InlineEditMultiSelect
                options={tenants.map((t) => ({ value: t.id, label: t.name }))}
                defaultValue={tenants.map((t) => t.id)}
                onSubmit={(val) =>
                  onSubmit(
                    "fleetControlCustomerIds",
                    tenants.filter((t) => val.includes(t.id))
                  )
                }
                canEdit={canEdit}
                hrefBase="https://fleetcontrol.nordcloudapp.com/?customerId="
                width="100%"
              />
            )}
          </DetailRow>
          <DetailRow title="Reporting start date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.fleetControlReportingStartDate}
              onSubmit={(val) =>
                onSubmit("fleetControlReportingStartDate", val)
              }
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Reporting end date">
            <InlineEditDatePicker
              clearable
              defaultValue={data?.fleetControlReportingEndDate}
              onSubmit={(val) => onSubmit("fleetControlReportingEndDate", val)}
              canEdit={canEdit}
            />
          </DetailRow>
          <Spacer height="1rem" />
        </Divider>

        <Spacer height="1rem" />
        <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
          Confluence Page Duplicator
        </Text>

        <ConfluenceDuplicator />

        {/* START JIRA */}
        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
            JIRA Space Codes
          </Text>
          <AddJiraProject
            refetch={fetchJiraProjects}
            fetchCustomer={fetchCustomer}
          />
          <Box>
            <Table
              columns={columns}
              data={jiraProjects.list || []}
              noDataText="No Jira Projects added."
            />
            {/* {jiraProjects.list?.length > 0 && (
              <QueryPagination count={jiraProjects.list.length} />
            )} */}
          </Box>
        </Divider>

        {/* END JIRA */}
      </Box>
    </LoaderWrap>
  );
}

export { ToolsAndIntegrations };
